import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ChevronLeft, Landmark, Home as HomeIcon } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { useLang } from "@/context/LanguageContext";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { setMeta, setJsonLd, removeJsonLd, setCanonical } from "@/lib/seo";

export default function AttractionPage() {
  const { attractionSlug } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/public/attraction/${attractionSlug}`)
      .then((r) => {
        const d = r.data;
        setData(d);
        const a = d.attraction;
        document.title = `Case vacanze e B&B vicino a ${a.name} | Quattro Accommodations`;
        setMeta("description", `${d.count} case vacanze e B&B vicino a ${a.name}, ${a.city}. Prenota diretto con il proprietario e dormi a pochi passi dalle attrazioni.`);
        setCanonical(`${window.location.origin}/case-vacanze-vicino-a/${attractionSlug}`);
        setJsonLd("ld-attraction", {
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": a.name,
          "description": a.intro,
          "address": { "@type": "PostalAddress", "addressLocality": a.city, "addressRegion": a.region, "addressCountry": "IT" },
          "geo": { "@type": "GeoCoordinates", "latitude": a.lat, "longitude": a.lng },
        });
        setJsonLd("ld-attraction-list", {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": d.properties.map((p, i) => ({
            "@type": "ListItem", "position": i + 1,
            "url": `${window.location.origin}/proprieta/${p.slug}`, "name": p.name,
          })),
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    return () => { removeJsonLd("ld-attraction"); removeJsonLd("ld-attraction-list"); };
  }, [attractionSlug]);

  if (loading) return <PublicLayout><div className="max-w-7xl mx-auto px-6 py-32 text-center text-brand-plum/50">Caricamento...</div></PublicLayout>;

  if (notFound || !data) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-brand-plum mb-3">Attrazione non trovata</h1>
        <Link to="/case-vacanze"><Button className="rounded-full bg-brand-plum text-white">Vedi tutte le case vacanze e i B&B</Button></Link>
      </div>
    </PublicLayout>
  );

  const a = data.attraction;
  return (
    <PublicLayout>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={a.image} alt={a.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-violet/95 via-brand-plum/60 to-brand-violet/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <Link to={`/case-vacanze/${data.properties[0]?.city ? data.properties[0].city.toLowerCase().replace(/\s+/g, "-") : ""}`} className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-brand-teal mb-4">
            <ChevronLeft size={15} /> {a.city}
          </Link>
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-3 flex items-center gap-1.5"><Landmark size={13} /> {a.region}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white max-w-3xl">{t("attraction.nearTitle", { name: a.name })}</h1>
          <p className="mt-4 text-white/85 max-w-2xl">{a.intro}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <p className="text-sm text-brand-plum/60 mb-6">{data.count} {data.count === 1 ? t("attraction.structure") : t("attraction.structures")} · {t("attraction.ordered")} {a.name}</p>
        {data.properties.length === 0 ? (
          <div className="text-center py-20 text-brand-plum/50">
            <HomeIcon size={40} className="mx-auto mb-3 text-brand-plum/30" />
            <p className="font-serif text-2xl text-brand-plum">{t("attraction.none")}</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.properties.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
